import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ClipboardList, Loader2, RefreshCw, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import adminService from '../../services/adminService';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

const methodBadge = (method) => {
  const m = method?.toUpperCase();
  if (m === 'GET') return <Badge className="bg-blue-100 text-blue-800 font-mono">{m}</Badge>;
  if (m === 'POST') return <Badge className="bg-green-100 text-green-800 font-mono">{m}</Badge>;
  if (m === 'PUT') return <Badge className="bg-yellow-100 text-yellow-800 font-mono">{m}</Badge>;
  if (m === 'DELETE') return <Badge className="bg-red-100 text-red-800 font-mono">{m}</Badge>;
  if (m === 'PATCH') return <Badge className="bg-purple-100 text-purple-800 font-mono">{m}</Badge>;
  return <Badge variant="secondary" className="font-mono">{method}</Badge>;
};

const statusCodeBadge = (code) => {
  if (!code) return null;
  const n = parseInt(code);
  if (n >= 200 && n < 300) return <Badge className="bg-green-100 text-green-800">{code}</Badge>;
  if (n >= 400 && n < 500) return <Badge className="bg-yellow-100 text-yellow-800">{code}</Badge>;
  if (n >= 500) return <Badge className="bg-red-100 text-red-800">{code}</Badge>;
  return <Badge variant="secondary">{code}</Badge>;
};

export default function AdminAuditLogsPage() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 25, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Filters
  const [userId, setUserId] = useState('');
  const [orgId, setOrgId] = useState('');
  const [entityType, setEntityType] = useState('');
  const [httpMethod, setHttpMethod] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const filter = { page, pageSize: 25 };
      if (userId) filter.userId = userId;
      if (orgId) filter.organizationId = orgId;
      if (entityType) filter.entityType = entityType;
      if (httpMethod) filter.httpMethod = httpMethod;
      if (dateFrom) filter.dateFrom = dateFrom;
      if (dateTo) filter.dateTo = dateTo;
      const result = await adminService.getAuditLogs(filter);
      setData(result);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  }, [page, userId, orgId, entityType, httpMethod, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const resetAndFetch = (setter, value) => {
    setter(value);
    setPage(1);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Audit Logs</h1>
            <p className="text-muted-foreground mt-1">Track all system activity and API calls</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchLogs}>
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Filter Logs</CardTitle>
            <div className="flex flex-wrap gap-3 pt-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm shrink-0">User ID</Label>
                <Input
                  className="w-28"
                  placeholder="User ID"
                  value={userId}
                  onChange={e => resetAndFetch(setUserId, e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm shrink-0">Org ID</Label>
                <Input
                  className="w-28"
                  placeholder="Org ID"
                  value={orgId}
                  onChange={e => resetAndFetch(setOrgId, e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm shrink-0">Entity</Label>
                <Input
                  className="w-32"
                  placeholder="e.g. User"
                  value={entityType}
                  onChange={e => resetAndFetch(setEntityType, e.target.value)}
                />
              </div>
              <Select value={httpMethod} onValueChange={v => resetAndFetch(setHttpMethod, v === 'all' ? '' : v)}>
                <SelectTrigger className="w-32"><SelectValue placeholder="All Methods" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  {HTTP_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Label className="text-sm">From</Label>
                <Input type="date" className="w-36" value={dateFrom} onChange={e => resetAndFetch(setDateFrom, e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">To</Label>
                <Input type="date" className="w-36" value={dateTo} onChange={e => resetAndFetch(setDateTo, e.target.value)} />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : data.items?.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>No audit logs found for the selected filters.</p>
              </div>
            ) : (
              <>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Method</TableHead>
                        <TableHead>Endpoint</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead>Entity ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>IP</TableHead>
                        <TableHead>Timestamp</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data.items || []).map((log, i) => (
                        <TableRow key={log.auditLogId ?? i}>
                          <TableCell>{methodBadge(log.httpMethod)}</TableCell>
                          <TableCell className="font-mono text-xs max-w-[200px] truncate" title={log.endpoint}>
                            {log.endpoint}
                          </TableCell>
                          <TableCell className="text-sm">{log.entityType}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{log.entityId}</TableCell>
                          <TableCell>{statusCodeBadge(log.statusCode)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{log.userName ?? log.userId}</TableCell>
                          <TableCell className="text-xs text-muted-foreground font-mono">{log.ipAddress}</TableCell>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {data.page} of {data.totalPages} ({data.total} records)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
